"use client";

import React from "react";
import { CollectionCard } from "modules/collections/details/model/collection";
import {
  ActionButtonsProps,
  CardActionButtons,
} from "modules/common/components/actions";
import { useCollectionActions } from "modules/collections/hooks/useCollectionActions";
import {
  useAddToTradeListModal,
  useEditCollectionCardModal,
  useRemoveFromTradeListModal,
} from "modules/collections/stores/useCollectionsStore";
import { useCardDetailsModal } from "modules/cards/hooks/useCardDetailsModal";
import { useMultiSelectStore } from "modules/common/stores/useMultiSelectStore";
import { primaryDisplayFinish } from "modules/cards/model/finishBehavior";
import { selectableInventoryIds } from "modules/common/selectableInventoryIds";
import type { BinderItemsRequest } from "modules/binder/model/binder";

interface CollectionCardActionButtonsProps
  extends Pick<ActionButtonsProps, "buttonClassName" | "icon"> {
  card: CollectionCard;
  binderRequest?: BinderItemsRequest;
  disabled?: boolean;
  className?: string;
  enableSelect?: boolean;
  showViewDetails?: boolean;
}

export function CollectionCardActionButtons({
  card,
  binderRequest,
  disabled = false,
  className,
  buttonClassName,
  icon,
  enableSelect = true,
  showViewDetails = true,
}: CollectionCardActionButtonsProps) {
  const hasSelection = useMultiSelectStore((s) => s.hasSelection());
  const isDisabled = disabled || hasSelection;
  const { openModal: openAddToTradeListModal } = useAddToTradeListModal();
  const { openModal: openRemoveModal } = useRemoveFromTradeListModal();
  const { openModal: openEditCollectionCardModal } =
    useEditCollectionCardModal();
  const { handleOpenCardDetailsByDataId } = useCardDetailsModal();
  const editableIds = selectableInventoryIds(card);
  const mutableCard = () => ({
    ...card,
    ids: editableIds,
    availableIds: editableIds,
    quantity: editableIds.length,
  });
  const handleEditCard = () => {
    if (editableIds.length === 0) return;
    openEditCollectionCardModal(mutableCard());
  };
  const { actions } = useCollectionActions({
    card,
    binderRequest,
    disabled: isDisabled,
    onAddToTradeList: () => {
      openAddToTradeListModal(card);
    },
    onRemoveFromTradeList: () => {
      if (editableIds.length > 0) openRemoveModal(mutableCard());
    },
    onViewCardDetails: showViewDetails
      ? () => {
          handleOpenCardDetailsByDataId(
            card.dataId,
            card.game,
            primaryDisplayFinish(card.finishes)
          );
        }
      : undefined,
    onEditCard: editableIds.length > 0 ? handleEditCard : undefined,
    enableSelect,
  });

  return (
    <CardActionButtons
      title={"Collection Card Actions"}
      actions={actions}
      disabled={isDisabled}
      className={className}
      buttonClassName={buttonClassName}
      icon={icon}
    />
  );
}
